import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "sos_cart_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        } catch {
            return [];
        }
    });
    const syncTimeoutRef = useRef(null);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        // Debounced sync to backend (only if user authenticated)
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            const token = localStorage.getItem("sos_token");
            if (!token) return;
            api.post("/me/cart", { items }).catch(() => {});
        }, 1500);
        return () => syncTimeoutRef.current && clearTimeout(syncTimeoutRef.current);
    }, [items]);

    const add = (product, qty = 1) => {
        const unit = Number(product.final_price ?? product.price ?? 0);
        const vat = Number(product.vat_rate ?? 23);
        setItems((prev) => {
            const found = prev.find((i) => i.product_id === product.id);
            if (found) {
                return prev.map((i) =>
                    i.product_id === product.id
                        ? { ...i, quantity: Math.min(i.quantity + qty, product.stock || 99) }
                        : i,
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    sku: product.sku,
                    name: product.name,
                    price: unit,
                    base_price: Number(product.price ?? 0),
                    vat_rate: vat,
                    campaign_name: product.campaign_name || null,
                    image_url: product.image_url,
                    stock: product.stock,
                    quantity: qty,
                },
            ];
        });
    };

    const update = (productId, qty) =>
        setItems((prev) =>
            prev
                .map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(0, qty) } : i))
                .filter((i) => i.quantity > 0),
        );
    const remove = (productId) =>
        setItems((prev) => prev.filter((i) => i.product_id !== productId));
    const clear = () => setItems([]);

    const totals = useMemo(() => {
        const count = items.reduce((s, i) => s + i.quantity, 0);
        const subtotal = items.reduce((s, i) => s + i.quantity * i.price, 0);
        const vatAmount = items.reduce(
            (s, i) => s + i.quantity * i.price * (Number(i.vat_rate ?? 23) / 100),
            0,
        );
        const totalWithVat = subtotal + vatAmount;
        return {
            count,
            subtotal,
            vatAmount: Math.round(vatAmount * 100) / 100,
            totalWithVat: Math.round(totalWithVat * 100) / 100,
        };
    }, [items]);

    return (
        <CartContext.Provider value={{ items, add, update, remove, clear, ...totals }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart outside provider");
    return ctx;
};
